import { useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UploadDropzone } from '@/components/tools/UploadDropzone';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { convertJpgToPng, absoluteApiUrl } from '@/lib/api';
import { downloadFromUrl, humanBytes } from '@/lib/download';
import { AlertCircle, CheckCircle2, Download, Image as ImageIcon, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import { TOOL_PAGE } from '@/constants/testIds';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Is this conversion lossless?', a: 'Yes. PNG is a lossless format, so no image data is thrown away. The output can be re-edited without any degradation.' },
    { q: 'Why is my PNG larger than the original JPG?', a: 'JPG uses lossy compression tuned for photographs; PNG uses lossless compression. For photos, PNG files are almost always larger. If size matters, keep it as JPG.' },
    { q: 'Do you support .jpeg files?', a: 'Yes. Both .jpg and .jpeg are accepted — they are the same format with different extensions.' },
    { q: 'What resolution is preserved?', a: 'The full resolution and pixel data of your original image are preserved. Only the container format changes.' },
    { q: 'Are files private?', a: 'Yes. Uploads are processed in isolation and deleted from disk right after conversion.' },
];

const HOW = [
    'Pick a JPG file from your phone or computer and drop it into the upload area. UtilityTool’s image pipeline uses Pillow — the same image library that powers a large chunk of the internet — to read your file, respect its EXIF orientation, and re-encode it as a PNG.',
    'PNG is a lossless format. That means the pixel values from your JPG are copied over exactly, with no additional degradation. Where the JPG stored the image using a compressed, human-eye-tuned encoding, the PNG stores every pixel value verbatim.',
    'Because there’s no re-quantization or resampling, the visual quality of the output is identical to the input. Zoom in as much as you like — nothing gets softer or blockier during the conversion.',
    'When conversion finishes, you get a Download button that streams the PNG straight to your browser. Our copy of the file is removed immediately.',
];
const BENEFITS = [
    'Converting a JPG to PNG opens up a few workflows that a plain JPG can’t handle. PNG supports transparency, so if you plan to edit the image — knocking out a background, layering it on another graphic, or turning part of it into a logo — PNG is the format your editing tools expect.',
    'PNG is also the safer choice when you’re going to open, tweak, and re-save the same image many times. Every JPG save nibbles a bit of quality; PNG never does. For anything you plan to edit repeatedly, work in PNG and only export to JPG when you’re done.',
    'Some workflows and printers strongly prefer PNG. Sticking to their spec makes your files easier to accept and less likely to be rejected by an automated intake pipeline.',
];
const TIPS = [
    'If you need transparency, remember to edit out the background in an image editor — the conversion alone doesn’t remove it.',
    'For very large photographs on the web, JPG or WebP will almost always be a better choice than PNG.',
    'Once you’re happy with the PNG, run it through the Image Compressor to keep the file size sensible.',
];

export default function JpgToPngPage() {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const reset = () => {
        setFile(null);
        setProgress(0);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const onFile = (f) => {
        setFile(f);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const run = async () => {
        if (!file) return;
        setStatus('uploading');
        setError('');
        setProgress(0);
        try {
            const envelope = await convertJpgToPng(file, setProgress);
            setResult(envelope);
            setStatus('done');
            toast.success('PNG ready to download.');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — JPG to PNG',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="JPG to PNG converter"
                description="Convert JPG images to lossless PNG in seconds. Free, private, no signup. Preserves resolution and EXIF orientation."
                canonical="/tools/jpg-to-png"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'JPG to PNG' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <ImageIcon className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            JPG to PNG converter
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Convert a JPG image to a lossless PNG. Full resolution and
                            orientation preserved.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Lossless</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">EXIF-aware</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Up to 25 MB</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <UploadDropzone
                            accept=".jpg,.jpeg,image/jpeg"
                            file={file}
                            onFile={onFile}
                            onClear={reset}
                            disabled={status === 'uploading'}
                            hint="Accepted formats: .jpg, .jpeg · Max 25 MB"
                        />
                        {status === 'uploading' ? (
                            <div className="mt-6">
                                <div className="flex items-center justify-between text-sm mb-2">
                                    <span className="font-medium text-slate-800">Uploading & converting…</span>
                                    <span className="text-slate-500">{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                        ) : null}
                        {status === 'error' && error ? (
                            <Alert variant="destructive" className="mt-6" data-testid={TOOL_PAGE.errorAlert}>
                                <AlertCircle className="w-4 h-4" />
                                <AlertTitle>Conversion failed</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}
                        {status === 'done' && result ? (
                            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={TOOL_PAGE.resultCard}>
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm font-medium">Conversion complete</span>
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-slate-900 truncate">{result.filename}</p>
                                        <p className="text-xs text-slate-500">{humanBytes(result.size_bytes)}</p>
                                    </div>
                                    <Button
                                        data-testid={TOOL_PAGE.downloadButton}
                                        onClick={() => downloadFromUrl(absoluteApiUrl(result.download_url), result.filename)}
                                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                                    >
                                        <Download className="w-4 h-4 mr-2" /> Download PNG
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={run}
                                disabled={!file || status === 'uploading'}
                                data-testid={TOOL_PAGE.runButton}
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                {status === 'uploading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Converting…
                                    </>
                                ) : (
                                    'Convert to PNG'
                                )}
                            </Button>
                            <Button variant="secondary" onClick={reset} className="h-11">
                                <RotateCcw className="w-4 h-4 mr-2" /> Reset
                            </Button>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Your image stays yours</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                Uploads are deleted immediately after conversion. We don’t
                                keep, log, or reuse your image in any way.
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Related</p>
                            <ul className="mt-2 space-y-1.5 text-sm">
                                <li><a href="/tools/image-compressor" className="text-[hsl(var(--primary))] hover:underline">Image Compressor</a></li>
                                <li><a href="/blog/jpg-vs-png-when-to-use-each" className="text-[hsl(var(--primary))] hover:underline">Guide: JPG vs PNG</a></li>
                                <li><a href="/blog/image-compression-explained" className="text-[hsl(var(--primary))] hover:underline">Guide: Image compression explained</a></li>
                            </ul>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
